import React, { useState } from 'react';
import { X, Smartphone, Shuffle, Loader2 } from 'lucide-react';
import { downloadCodeApi } from '../lib/api';

interface Props {
  mediaFileId: string;
  filename: string;
  onClose: () => void;
  onDownload: (downloadUrl: string, downloadFilename: string) => void;
}

/**
 * Asks the buyer which campaign type the download is for, then generates
 * the appropriate 8-char download code and triggers the download with the
 * renamed file (code-original_filename).
 *
 * Editors / admins / QC reviewers never see this modal — direct download.
 */
export function DownloadCodeModal({ mediaFileId, filename, onClose, onDownload }: Props) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [generated, setGenerated] = useState<{ code: string; download_filename: string; download_url: string } | null>(null);

  const handleSelect = async (code_type: 'normal' | 'flexible') => {
    setError(null);
    setSubmitting(true);
    try {
      const resp = await downloadCodeApi.generate(mediaFileId, code_type);
      const data = resp.data?.data;
      if (!data?.code) throw new Error('No code returned');
      setGenerated(data);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to generate code');
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirmDownload = () => {
    if (!generated) return;
    onDownload(generated.download_url, generated.download_filename);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-card border border-border rounded-lg w-full max-w-lg shadow-xl">
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <h2 className="text-base font-semibold">Download Creative</h2>
          <button onClick={onClose} className="p-1 rounded hover:bg-muted transition-colors">
            <X size={18} />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {!generated ? (
            <>
              <p className="text-sm text-muted-foreground">
                Which type of campaign is this for?
              </p>
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => handleSelect('normal')}
                  disabled={submitting}
                  className="flex flex-col items-center gap-2 p-4 border-2 border-border rounded-lg hover:border-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors disabled:opacity-50"
                >
                  <Smartphone size={28} className="text-blue-600" />
                  <span className="font-semibold text-sm">Normal Campaign</span>
                  <span className="text-[11px] text-muted-foreground text-center">Most ads (one creative per ad)</span>
                </button>
                <button
                  onClick={() => handleSelect('flexible')}
                  disabled={submitting}
                  className="flex flex-col items-center gap-2 p-4 border-2 border-border rounded-lg hover:border-purple-500 hover:bg-purple-50 dark:hover:bg-purple-900/20 transition-colors disabled:opacity-50"
                >
                  <Shuffle size={28} className="text-purple-600" />
                  <span className="font-semibold text-sm">Flex / Dynamic</span>
                  <span className="text-[11px] text-muted-foreground text-center">Multiple creatives in one ad</span>
                </button>
              </div>
              <p className="text-[11px] text-muted-foreground border-t border-border pt-3">
                A unique 8-character code will be added to the filename so this creative can be tracked back from any campaign.
              </p>
            </>
          ) : (
            <>
              <div className="rounded-lg border border-green-300 dark:border-green-700 bg-green-50 dark:bg-green-900/20 p-4">
                <p className="text-xs uppercase tracking-wide text-green-700 dark:text-green-300 font-semibold">Generated code</p>
                <p className="text-2xl font-mono font-bold text-green-800 dark:text-green-200 mt-1">{generated.code}</p>
                <p className="text-[11px] text-green-700 dark:text-green-400 mt-2 break-all">
                  Filename: <span className="font-mono">{generated.download_filename}</span>
                </p>
              </div>
              <p className="text-xs text-muted-foreground">
                Save this code — you'll need it in your campaign name to track the creative later.
              </p>
            </>
          )}

          {submitting && (
            <div className="flex items-center justify-center py-4 text-sm text-muted-foreground">
              <Loader2 size={16} className="mr-2 animate-spin" /> Generating code…
            </div>
          )}
          {error && (
            <div className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 border border-red-300 dark:border-red-700 rounded p-3">
              {error}
            </div>
          )}
        </div>

        <div className="px-5 py-3 border-t border-border flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm rounded border border-border hover:bg-muted transition-colors"
          >
            Cancel
          </button>
          {generated && (
            <button
              onClick={handleConfirmDownload}
              className="px-4 py-2 text-sm rounded bg-blue-600 hover:bg-blue-700 text-white transition-colors"
            >
              Download
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
